import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Megaphone, Send, User, Users } from 'lucide-react';
import { PortalRosterRow, portalMentorService } from '../../../services/api';
import { Avatar, EmptyState, HealthBadge, fa, fullName, inputClass } from './MentorUI';

interface Cohort {
  id: string;
  name: string;
  capacity: number;
  participants: number;
}

interface Props {
  onError: (err: any, fallback: string) => void;
  onFlash: (message: string) => void;
}

type Mode = 'cohorts' | 'people';

/**
 * پیام منتور به گروه‌ها یا به افراد مشخص.
 *
 * پیام به‌صورت اعلان داخل پرتال می‌رسد. پیامک عمداً از اینجا فرستاده نمی‌شود:
 * فقط فهرست شماره‌ها برگردانده می‌شود تا منتور با پنل پیامکی خودش بفرستد و
 * متن محرمانه‌ای هم از مسیر پیامک بیرون نرود.
 */
export const MentorAnnounceTab: React.FC<Props> = ({ onError, onFlash }) => {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [roster, setRoster] = useState<PortalRosterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [mode, setMode] = useState<Mode>('cohorts');
  const [selectedCohorts, setSelectedCohorts] = useState<string[]>([]);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [personSearch, setPersonSearch] = useState('');

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [wantPhones, setWantPhones] = useState(false);
  const [phones, setPhones] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        portalMentorService.cohorts(),
        portalMentorService.roster().catch(() => [] as PortalRosterRow[]),
      ]);
      setCohorts(c);
      setRoster(r);
    } catch (err: any) {
      onError(err, 'دریافت گروه‌ها انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCohort = (id: string) =>
    setSelectedCohorts((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const togglePerson = (id: string) =>
    setSelectedPeople((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const visiblePeople = useMemo(() => {
    const term = personSearch.trim();
    if (!term) return roster;
    return roster.filter(
      (row) => fullName(row.user).includes(term) || (row.user.phone || '').includes(term),
    );
  }, [roster, personSearch]);

  /**
   * تعداد مخاطب. در حالت گروهی، انتخاب‌نکردن یعنی همهٔ گروه‌ها؛ در حالت فردی،
   * انتخاب‌نکردن یعنی هیچ‌کس — چون «پیام به هیچ‌کس» با «پیام به همه» اشتباه
   * گرفته شود، فاجعه است.
   */
  const reach =
    mode === 'people'
      ? selectedPeople.length
      : selectedCohorts.length
        ? cohorts
            .filter((c) => selectedCohorts.includes(c.id))
            .reduce((a, c) => a + c.participants, 0)
        : cohorts.reduce((a, c) => a + c.participants, 0);

  const send = async () => {
    if (!title.trim()) {
      onError(null, 'عنوان پیام را بنویسید.');
      return;
    }
    if (mode === 'people' && !selectedPeople.length) {
      onError(null, 'حداقل یک نفر را انتخاب کنید.');
      return;
    }
    if (!window.confirm('این پیام برای ' + fa(reach) + ' نفر فرستاده شود؟')) return;

    setBusy(true);
    setPhones([]);
    try {
      const result = await portalMentorService.announce({
        title: title.trim(),
        body: body.trim() || undefined,
        ...(mode === 'people'
          ? { participantIds: selectedPeople }
          : selectedCohorts.length
            ? { cohortIds: selectedCohorts }
            : {}),
        alsoSms: wantPhones,
      });
      setPhones(result.phones ?? []);
      setTitle('');
      setBody('');
      onFlash('پیام برای ' + fa(result.sent) + ' نفر ثبت شد.');
    } catch (err: any) {
      onError(err, 'ارسال پیام انجام نشد.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="py-10 text-center text-sm font-bold text-gray-400">در حال بارگذاری…</p>;
  }

  if (!cohorts.length && !roster.length) {
    return (
      <EmptyState
        icon={<Users className="h-5 w-5" />}
        title="گروهی به شما سپرده نشده"
        body="پس از اینکه مدیریت دوره گروهی را به شما بسپارد، می‌توانید برایشان پیام بفرستید."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-1 flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-blue-600" />
          <p className="text-sm font-bold text-gray-800">پیام به شرکت‌کنندگان من</p>
        </div>
        <p className="mb-3 text-[11.5px] font-bold text-gray-500">
          پیام به‌صورت اعلان در پرتال شرکت‌کننده می‌نشیند. فقط کسانی که به شما سپرده شده‌اند
          اینجا هستند.
        </p>

        <div className="mb-3 flex gap-1.5">
          {(
            [
              ['cohorts', 'به گروه', <Users key="c" className="h-3.5 w-3.5" />],
              ['people', 'به افراد', <User key="p" className="h-3.5 w-3.5" />],
            ] as const
          ).map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              className={
                'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition ' +
                (mode === key
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 text-gray-600 hover:border-gray-400')
              }
            >
              {icon}
              {label}
            </button>
          ))}
        </div>

        {mode === 'cohorts' ? (
          <>
            <p className="mb-1.5 text-xs font-bold text-gray-600">
              مخاطب {selectedCohorts.length ? '' : '(هیچ‌کدام انتخاب نشده = همهٔ گروه‌های شما)'}
            </p>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {cohorts.map((cohort) => {
                const on = selectedCohorts.includes(cohort.id);
                return (
                  <button
                    key={cohort.id}
                    type="button"
                    onClick={() => toggleCohort(cohort.id)}
                    className={
                      'rounded-lg border px-3 py-1.5 text-xs font-bold transition ' +
                      (on
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400')
                    }
                  >
                    {cohort.name}
                    <span className="mr-1 text-[10.5px] font-bold text-gray-400">
                      {fa(cohort.participants)} نفر
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <input
                value={personSearch}
                onChange={(e) => setPersonSearch(e.target.value)}
                placeholder="جستجوی نام یا شماره…"
                className={inputClass + ' w-auto min-w-[220px] flex-1'}
              />
              <button
                type="button"
                onClick={() =>
                  setSelectedPeople(
                    selectedPeople.length === visiblePeople.length
                      ? []
                      : visiblePeople.map((row) => row.id),
                  )
                }
                className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-bold text-gray-600 hover:border-gray-400"
              >
                {selectedPeople.length === visiblePeople.length && visiblePeople.length
                  ? 'برداشتن همه'
                  : 'انتخاب همهٔ نتایج'}
              </button>
              <span className="text-xs font-bold text-gray-500">
                {fa(selectedPeople.length)} نفر انتخاب شده
              </span>
            </div>

            <div className="mb-3 max-h-72 overflow-y-auto rounded-lg border border-gray-200">
              {!visiblePeople.length ? (
                <p className="py-8 text-center text-xs font-bold text-gray-400">
                  کسی با این جستجو پیدا نشد.
                </p>
              ) : (
                visiblePeople.map((row) => {
                  const on = selectedPeople.includes(row.id);
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => togglePerson(row.id)}
                      className={
                        'flex w-full items-center gap-2.5 border-b border-gray-100 px-3 py-2 text-right transition last:border-b-0 ' +
                        (on ? 'bg-blue-50' : 'hover:bg-gray-50')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        readOnly
                        className="h-4 w-4 shrink-0 rounded border-gray-300"
                      />
                      <Avatar user={row.user} size="sm" tone="#1D4ED8" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-bold text-gray-900">
                          {fullName(row.user)}
                        </span>
                        <span className="block text-[10.5px] font-bold text-gray-400">
                          {row.cohort?.name} · {row.user.phone || 'بدون شماره'}
                        </span>
                      </span>
                      <HealthBadge health={row.health} />
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}

        <div className="grid gap-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="عنوان پیام"
            maxLength={200}
            className={inputClass}
          />
          <textarea
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="متن پیام (اختیاری)"
            maxLength={2000}
            className={inputClass}
          />
        </div>

        <label className="mt-2 flex items-center gap-2 text-xs font-bold text-gray-600">
          <input
            type="checkbox"
            checked={wantPhones}
            onChange={(e) => setWantPhones(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          فهرست شماره‌ها را هم بده (برای ارسال پیامک از پنل خودتان)
        </label>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy || !title.trim() || !reach}
            onClick={send}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            ارسال به {fa(reach)} نفر
          </button>
          {(mode === 'cohorts' ? selectedCohorts.length : selectedPeople.length) ? (
            <button
              type="button"
              onClick={() =>
                mode === 'cohorts' ? setSelectedCohorts([]) : setSelectedPeople([])
              }
              className="text-xs font-bold text-gray-500 hover:text-gray-700"
            >
              پاک‌کردن انتخاب
            </button>
          ) : null}
        </div>
      </div>

      {phones.length ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-bold text-gray-800">{fa(phones.length)} شمارهٔ مخاطب</p>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard
                  ?.writeText(phones.join(String.fromCharCode(10)))
                  .then(() => onFlash('شماره‌ها کپی شد.'))
                  .catch(() => onError(null, 'کپی انجام نشد.'));
              }}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-700"
            >
              <Copy className="h-3.5 w-3.5" />
              کپی همه
            </button>
          </div>
          <textarea
            readOnly
            rows={5}
            value={phones.join(String.fromCharCode(10))}
            className={inputClass + ' font-mono text-xs'}
          />
        </div>
      ) : null}
    </div>
  );
};
